import { useState, useCallback, useEffect } from 'react';
import { saveAssessment as saveAssessmentApi, getAssessmentSummary } from '../components/API';

const SUMMARY_CACHE_KEY = 'bloodeye_assessment_summary';
const PREV_SUMMARY_CACHE_KEY = 'bloodeye_assessment_prev_summary';

function getCachedSummary() {
    try {
        const raw = sessionStorage.getItem(SUMMARY_CACHE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
}

function setCachedSummary(summary) {
    try { sessionStorage.setItem(SUMMARY_CACHE_KEY, JSON.stringify(summary)); } catch {}
}

function getCachedPrevSummary() {
    try {
        const raw = sessionStorage.getItem(PREV_SUMMARY_CACHE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
}

function setCachedPrevSummary(prev) {
    try {
        if (prev) sessionStorage.setItem(PREV_SUMMARY_CACHE_KEY, JSON.stringify(prev));
        else sessionStorage.removeItem(PREV_SUMMARY_CACHE_KEY);
    } catch {}
}

export default function useAssessment() {
    const [answers, setAnswers] = useState({});
    const [summary, setSummary] = useState(() => getCachedSummary());
    const [prevSummary, setPrevSummary] = useState(() => getCachedPrevSummary());
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const loadSummary = useCallback(async (patientId = null) => {
        if (!summary) setLoading(true);
        try {
            const result = await getAssessmentSummary(patientId);
            setSummary(result.summary);
            setCachedSummary(result.summary);
            setPrevSummary(result.prev_summary || null);
            setCachedPrevSummary(result.prev_summary || null);
        } catch (e) {
            console.error('Failed to load assessment summary:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    const setAnswer = useCallback((itemId, value) => {
        setAnswers(prev => ({ ...prev, [itemId]: value }));
    }, []);

    const clearAnswers = useCallback(() => {
        setAnswers({});
    }, []);

    const submitAssessment = useCallback(async (assessmentId) => {
        setSubmitting(true);
        setError(null);
        try {
            const result = await saveAssessmentApi(assessmentId, answers);
            setSummary(result.summary);
            setCachedSummary(result.summary);
            return result;
        } catch (e) {
            setError(e.message || 'Failed to save assessment');
            throw e;
        } finally {
            setSubmitting(false);
        }
    }, [answers]);

    const hasExistingAssessment = useCallback((assessmentId) => {
        if (!summary || !summary.assessments) return false;
        return assessmentId in summary.assessments;
    }, [summary]);

    const getExistingDate = useCallback((assessmentId) => {
        if (!summary?.assessments?.[assessmentId]) return null;
        return summary.assessments[assessmentId].completed_at;
    }, [summary]);

    return {
        answers,
        summary,
        prevSummary,
        loading,
        submitting,
        error,
        loadSummary,
        setAnswer,
        clearAnswers,
        submitAssessment,
        hasExistingAssessment,
        getExistingDate,
    };
}
